import React from "react"

import {
  TypographyH2,
  TypographyH3,
  TypographyP,
  TypographyList,
  TypographyInlineCode
} from "@javascript/components/ui/typography"
import { CodeBlock } from "@javascript/components/ui/code-block"

export function ContentAndFormsPage() {
  const contentStructureExample = `{
  "version": 2,
  "couple": {
    "partners": [
      { "id": "partner-1", "name": "Maya" },
      { "id": "partner-2", "name": "Jules" }
    ],
    "cover_asset_id": "opaque-asset-token"
  },
  "events": [
    {
      "id": "ceremony",
      "kind": "ceremony",
      "title": "Ceremony",
      "starts_at": "2027-03-12T16:30",
      "timezone": "Asia/Jakarta",
      "venue": { "name": "Garden Pavilion" }
    }
  ],
  "story": {
    "milestones": [
      { "id": "proposal", "date": "2026-01-02", "title": "The proposal" }
    ]
  }
}`

  return (
    <div className="space-y-8">
      <div>
        <TypographyH2>Canonical Content & Schema-Generated Forms</TypographyH2>
        <TypographyP>
          Wedding facts belong to the invitation, independently of any theme. A versioned platform schema validates the document and generates the host editor; blocks only present it.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Three structural primitives</TypographyH3>
        <TypographyList>
          <li><strong>Field</strong> — a typed scalar such as text, rich text, date, datetime, URL, choice, timezone, or asset reference</li>
          <li><strong>Object</strong> — named children such as an event's venue</li>
          <li><strong>Collection</strong> — ordered repeatable objects with stable opaque IDs, minimums, and optional maximums</li>
        </TypographyList>
        <TypographyP>
          Those primitives express separate partners, multiple events and venues, story milestones, wedding-party members, gallery photos, travel details, FAQs, and registries without changing the theme contract.
        </TypographyP>
        <CodeBlock code={contentStructureExample} language="json" />
      </div>

      <div>
        <TypographyH3>Forms come from the invitation schema</TypographyH3>
        <TypographyP>
          <TypographyInlineCode>InvitationContent::Catalog</TypographyInlineCode> is the one definition of editable wedding facts. The host editor recursively renders its fields, objects, and collections, and server validation returns errors at exact nested paths.
        </TypographyP>
        <TypographyP>As a theme designer, you only:</TypographyP>
        <TypographyList>
          <li>Declare a platform block instance in <TypographyInlineCode>theme.json</TypographyInlineCode></li>
          <li>Place its ID in <TypographyInlineCode>template.liquid</TypographyInlineCode></li>
        </TypographyList>
        <TypographyP>
          Adding or removing a block does not add, remove, or rename the couple's form fields. Switching themes preserves the full content document.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>One coercion and validation boundary</TypographyH3>
        <TypographyP>
          <TypographyInlineCode>InvitationContent::Document</TypographyInlineCode> exposes immutable symbol-keyed Ruby data; persisted JSON and HTTP parameters remain string-keyed. <TypographyInlineCode>InvitationContent::Submission</TypographyInlineCode> normalizes, sanitizes, validates, scopes asset tokens, and commits atomically.
        </TypographyP>
        <TypographyP>RSVPs and uploaded files remain relational workflow records rather than being embedded into the content JSON.</TypographyP>
      </div>

      <div>
        <TypographyH3>Next steps</TypographyH3>
        <TypographyP>
          See the <a href="/developers/theme-manifest" className="text-primary hover:underline">theme.json Reference</a> for block declarations and the <a href="/developers/block-reference" className="text-primary hover:underline">Block Reference</a> for available presentation capabilities.
        </TypographyP>
      </div>
    </div>
  )
}
